import de from "./de.json";
import en from "./en.json";

/**
 * Translation plumbing for the saas-admin console.
 *
 * The dictionaries are keyed by their GERMAN source text, not by symbolic ids
 * (`"Abbrechen"`, not `"common.cancel"`). Two reasons: the templates stay
 * readable — `<%= _lcs( 'Abbrechen' ) %>` says what it renders — and a missing
 * translation degrades to correct German instead of to a naked key, because
 * IF.I18n returns the key when it finds nothing. `de.json` is therefore an
 * identity map: it is the canonical inventory of every translatable string and
 * the file a translator works from.
 *
 * Templates do not import anything from here — IF.View injects `_lcs` (plus
 * `_lcn` for numbers and `_lcd` for dates) into every render. This module is
 * for the plain JS that renders outside a template: label maps, GridJS column
 * headers, toasts, confirm dialogs.
 */

/**
 * The languages the console ships. `label` is deliberately written in its own
 * language — somebody looking for English scans for "English", not "Englisch".
 */
export const LANGUAGES = [
    { code : "de", label : "Deutsch" },
    { code : "en", label : "English" }
];

/** Source language: the one every dictionary key is written in. */
export const DEFAULT_LANGUAGE = "de";

export const DICTIONARIES = { de, en };

// Set by App.initL18n() as soon as the IF.I18n instance exists. Everything
// below degrades to the German key while it is null, which is what the very
// first module-evaluation pass sees.
let instance = null;

export function registerI18n( i18n )
{
    instance = i18n;
}

/**
 * Translate a German source string.
 *
 * @param {string} key       The German text, exactly as it appears in de.json.
 * @param {object} [params]  Interpolation values for `{placeholder}` slots;
 *                           a `count` also selects the plural form.
 * @returns {string}
 */
export function _lcs( key, params = {} )
{
    return instance ? instance.t( key, params ) : key;
}

/**
 * A platform catalog list from the API (Gästemappe modules, event categories,
 * price types, …) in the console's language. The API words its catalogs in
 * German, and those titles are dictionary keys — so a list is run through
 * _lcs once, right after it is loaded, and every screen reading it shows the
 * console's language (Funktionstest F18). The German originals stay
 * available under `source` — for whatever is written INTO content, which is
 * German (a module's prefilled title).
 *
 * @param {Array<object>} list
 * @param {string[]} [fields]  The text fields to translate.
 * @returns {Array<object>}
 */
export function localizeCatalog( list, fields = [ "title", "description" ] )
{
    return ( Array.isArray( list ) ? list : [] ).map( ( entry ) =>
    {
        const out = { ...entry, source : {} };
        fields.forEach( ( field ) =>
        {
            if ( typeof out[ field ] === "string" && out[ field ] )
            {
                out.source[ field ] = out[ field ];
                out[ field ] = _lcs( out[ field ] );
            }
        } );
        return out;
    } );
}

export function currentLanguage()
{
    return instance ? instance.getCurrentLanguage() : DEFAULT_LANGUAGE;
}

/**
 * Locale tag for Intl (dates, numbers, collation). The bare language code is
 * enough here: "de" formats dates and thousands separators identically to
 * "de-DE", and the console has no region-specific variants.
 */
export function locale()
{
    return currentLanguage();
}

export function isSupportedLanguage( code )
{
    return LANGUAGES.some( ( lang ) => lang.code === code );
}

/**
 * Pick a language we actually have a dictionary for: an explicit preference
 * first, then the browser's list, then German.
 *
 * IF.I18n's own browser detection is not enough — it accepts any valid ISO
 * code, so a French browser would leave the console in "fr" with an empty
 * dictionary and an unselectable entry in the switcher.
 */
export function resolveLanguage( preferred = null )
{
    const candidates = [ preferred ];

    if ( typeof navigator !== "undefined" )
    {
        if ( Array.isArray( navigator.languages ) ) candidates.push( ...navigator.languages );
        if ( navigator.language ) candidates.push( navigator.language );
    }

    for ( const candidate of candidates )
    {
        if ( !candidate ) continue;
        const code = String( candidate ).toLowerCase().split( "-" )[ 0 ];
        if ( isSupportedLanguage( code ) ) return code;
    }

    return DEFAULT_LANGUAGE;
}
